import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { BaseHttpService } from './base-http.service';
import { Observable } from 'rxjs';
import { siQuoteUrls } from '../../../environments/environment';
import { CollaborationHubData } from '../../models/dtos/Classes/CollaborationHubData';
import { PublishRequest } from '../../models/dtos/Classes/PublishRequest';
import { AccessRequestModel } from '../../models/dtos/Classes/AccessRequestModel';
import { GetAccessRequestsRequest } from '../../models/dtos/Classes/GetAccessRequestsRequest';
import { AcceptAccessRequest, AccessRequest, DeclineAccessResponse, CollaborationDefinition, DeclineAccessRequest, VerifyCollaboratorRequest, OpenEstimateResponse, EditCollabRequest, DiscardCollabRequest, EnableCollaborationRequest, DisableCollaborationRequest, VerifyCollaboratorsRequest, VerifyCollaboratorResultItem, ModifyCollaborationRequest, TurnoverToSalesResponse, TurnoverToSalesRequest, CollaborationInfo } from '../../models/dtos';
import { withLocalErrorHandling, withSilentBackgroundRequest } from '../../core/errors/http-context-tokens';

@Injectable({
  providedIn: 'root',
})
export class CollaborationApiService {
  
  constructor(private readonly baseHttp: BaseHttpService
    ) { }

  //#region Get Collaboration data
  getCollaborationHubData(isSilent : boolean = false): Observable<CollaborationHubData> {
    const context = isSilent ? withSilentBackgroundRequest() : undefined;
    return this.baseHttp.get<CollaborationHubData>(`${siQuoteUrls.collaboration}/GetCollaborationHubData`, null, { context });
  }

  getCollaborationDefinition(): Observable<CollaborationDefinition>{
    return this.baseHttp.get<CollaborationDefinition>(`${siQuoteUrls.collaboration}/GetCollaborationDefinition`, null, { context: withSilentBackgroundRequest() });
  }

  //#endregion

  //#region Collaboration Actions
  editCollabEstimate(request: EditCollabRequest, suppressGlobalError: boolean = false): Observable<OpenEstimateResponse> {
    const context = suppressGlobalError ? withLocalErrorHandling() : undefined;
    return this.baseHttp.post<OpenEstimateResponse>(`${siQuoteUrls.collaboration}/EditCollabEstimate`, request, { context });
  }

  publishChanges(request: PublishRequest, suppressGlobalError: boolean = false): Observable<boolean>{
    const context = suppressGlobalError ? withLocalErrorHandling() : undefined;
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/PublishChanges`, request, { context });
  }

  discardChanges(request: DiscardCollabRequest, suppressGlobalError: boolean = false): Observable<OpenEstimateResponse> {
    const context = suppressGlobalError ? withLocalErrorHandling() : undefined;
    return this.baseHttp.post<OpenEstimateResponse>(`${siQuoteUrls.collaboration}/DiscardChanges`, request, { context });
  }

  acceptAccessRequestAndPublish(request: PublishRequest): Observable<boolean> {
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/AcceptAccessRequestAndPublish`, request);
  }

  acceptAccessRequestAndDiscard(request: DiscardCollabRequest, suppressGlobalError: boolean = false): Observable<OpenEstimateResponse> {
    const context = suppressGlobalError ? withLocalErrorHandling() : undefined;
    return this.baseHttp.post<OpenEstimateResponse>(`${siQuoteUrls.collaboration}/AcceptAccessRequestAndDiscard`, request, { context });
  }

  enableCollaboration(request: EnableCollaborationRequest): Observable<CollaborationHubData>{
    return this.baseHttp.post<CollaborationHubData>(`${siQuoteUrls.collaboration}/EnableCollaboration`, request);
  }

  disableCollaboration(request: DisableCollaborationRequest): Observable<boolean>{
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/DisableCollaboration`, request);
  }

  modifyCollaboration(request: ModifyCollaborationRequest): Observable<CollaborationHubData>{
    return this.baseHttp.post<CollaborationHubData>(`${siQuoteUrls.collaboration}/ModifyCollaboration`, request);
  }

  turnoverToSales(request: TurnoverToSalesRequest, suppressGlobalError: boolean = false): Observable<TurnoverToSalesResponse>{
    const context = suppressGlobalError ? withLocalErrorHandling() : undefined;
    return this.baseHttp.post<TurnoverToSalesResponse>(`${siQuoteUrls.collaboration}/TurnoverToSales`, request, { context });
  }

  //#endregion

  //#region Access Requests
  raiseAccessRequest(request: AccessRequest) : Observable<boolean>{
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/RaiseAccessRequest`, request);
  }

  acceptAccessRequest(request: AcceptAccessRequest) : Observable<boolean>{
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/AcceptAccessRequest`, request);
  }

  declineAccessRequest(request: DeclineAccessRequest) : Observable<boolean>{
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/DeclineAccessRequest`, request);
  }
  
  getAccessRequests(request: GetAccessRequestsRequest, context?: HttpContext): Observable<AccessRequestModel[]>{
    return this.baseHttp.post<AccessRequestModel[]>(`${siQuoteUrls.collaboration}/GetAccessRequests`, request, { context });
  }

  getAccessRequestResponse(estimateId: string, requestedBy: string, context?: HttpContext): Observable<DeclineAccessResponse>{
    return this.baseHttp.get<DeclineAccessResponse>(`${siQuoteUrls.collaboration}/GetAccessRequestResponse?estimateId=${estimateId}&requestedBy=${requestedBy}`, null, { context });
  }

  //#endregion

  //#region Verify Collaborators

  verifyCollaborator(request: VerifyCollaboratorRequest): Observable<boolean> {
    return this.baseHttp.post<boolean>(`${siQuoteUrls.collaboration}/VerifyCollaborator`, request);
  }

  verifyCollaborators(request: VerifyCollaboratorsRequest): Observable<VerifyCollaboratorResultItem[]> {
    return this.baseHttp.post<VerifyCollaboratorResultItem[]>(`${siQuoteUrls.collaboration}/VerifyCollaborators`, request);
  }

  //#endregion

  getCollaborationInfo(): Observable<CollaborationInfo> {
    return this.baseHttp.get<CollaborationInfo>(`${siQuoteUrls.collaboration}/GetCollaborationInfo`, null, { context: withSilentBackgroundRequest() });
  }

  getIsGOCRestricted(): Observable<boolean> {
    const context = withLocalErrorHandling();
    return this.baseHttp.get<boolean>(`${siQuoteUrls.collaboration}/IsGOCRestricted`, null, { context });
  }

}
